import React from 'react'
import Two from 'two.js'

const SIZE = 20;
const HALF_SIZE = SIZE / 2;
const SPEED = 200; // pixels per second
const PXL_PER_MS = SPEED / 1000.0; // pixels per millisecond

const snap = (number) => {
  return number - (number % SIZE);
}

class Snake extends React.Component {

  constructor (props) {
    super(props);
    this.state = {};

    this.two = new Two({
      type: Two.Types.canvas,
      fullscreen: true
    });

    this.animate = this.animate.bind(this);
    this.onWindowKey = this.onWindowKey.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
  }

  componentDidMount() {
    const two = this.two;
    const stage = this.refs.stage;
    two.appendTo(stage);
    this.init();

    two.bind('update', this.animate).play();
    window.addEventListener('keydown', this.onWindowKey);
    stage.addEventListener('touchstart', this.onTouchStart);
  }

  componentWillUnmount() {
    const stage = this.refs.stage;
    window.removeEventListener('keydown', this.onWindowKey);
    stage.removeEventListener('touchstart', this.onTouchStart);
  }

  onTouchStart(event) {
    if (this.alive === false) {
      this.init();
      return;
    }
    const {x, y} = this.head.translation;
    const {clientX, clientY} = event.touches[0];
    if (this.direction === 'up' || this.direction === 'down') {
      this.setDirection(clientX - x > 0 ? 'right' : 'left');
    } else {
      this.setDirection(clientY - y > 0 ? 'down' : 'up');
    }
  }

  onWindowKey(event) {
    switch (event.key) {
      case 'ArrowDown': this.setDirection('down'); break;
      case 'ArrowUp': this.setDirection('up'); break;
      case 'ArrowLeft': this.setDirection('left'); break;
      case 'ArrowRight': this.setDirection('right'); break;
      case ' ': if (this.alive === false) this.init(); break;
      default:
        return;
    }
  }

  setDirection(newDirection) {
    if (this.direction === newDirection) {
      return;
    }

    // add tail anchor
    if (this.tailLength > 0) {
      this.tailAnchorHistory.unshift({
        x: this.head.translation.x,
        y: this.head.translation.y,
        direction: this.direction
      });
    }
    
    this.direction = newDirection;
  }

  init() {
    const two = this.two;
    two.clear();

    var head = this.head = two.makeRectangle(snap(two.width / 2), snap(two.height / 2), SIZE, SIZE);
    head.fill = 'rgba(10, 210, 255, 1)';
    head.noStroke();

    this.makeApple();

    this.tailLength = 0;
    this.tailAnchorHistory = [];
    this.tail = undefined;
    this.alive = true;
    this.direction = 'up';
  }

  makeApple() {
    const two = this.two;
    const x = (Math.random() * (two.width - SIZE)) + HALF_SIZE;
    const y = (Math.random() * (two.height - SIZE)) + HALF_SIZE;
    var apple = this.apple = two.makeRectangle(x, y, SIZE, SIZE);
    apple.fill = 'rgba(235, 28, 0, 0.90)';
    apple.noStroke();
  }

  // distance moved since last tick
  calculateDistance(timeDelta) {
    return timeDelta * PXL_PER_MS;
  }

  isCollision() {
    const {x, y} = this.head.translation;
    const {width, height} = this.two;

    // tail collision
    if (this.tail !== undefined) {
      const vertices = this.tail.vertices;
      for (let i = 0; i < vertices.length - 1; i++) {
        const v1 = vertices[i];
        const v2 = vertices[i + 1];
        if (Math.abs(y - v1.y) < SIZE) {
          // around the same y, check if we're between the x's
          if (x > Math.min(v1.x, v2.x) && x < Math.max(v1.x, v2.x)) return true;
        }
        if (Math.abs(x - v1.x) < SIZE) {
          // around the same x, check if we're between the y's
          if (y > Math.min(v1.y, v2.y) && y < Math.max(v1.y, v2.y)) return true;
        }
      }
    }

    // window bounds
    return (
      x - HALF_SIZE <= 0 ||
      x + HALF_SIZE >= width  ||
      y - HALF_SIZE <= 0 ||
      y + HALF_SIZE >= height
    );
  }

  eatApple() {
    const {x, y} = this.head.translation;
    const apple = this.apple;
    // if we're nowhere near the apple, continue
    if (Math.abs(x - apple.translation.x) > HALF_SIZE || 
        Math.abs(y - apple.translation.y) > HALF_SIZE) {
      return;
    }

    // grow
    this.tailLength = this.tailLength + 1;

    apple.remove();
    this.makeApple();
  }

  drawTail() {
    if (this.tailLength === 0) {
      return;
    }

    const tailAnchorHistory = this.tailAnchorHistory;
    const tailAnchors = [];
    
    // always start with head
    const totalLength = this.tailLength * SIZE;
    const tailStart = { x: this.head.translation.x, y: this.head.translation.y, direction: this.direction };
    tailAnchors.push(new Two.Anchor(tailStart.x, tailStart.y));

    let historyUsed = 0;
    let lengthLeft = totalLength;
    let lastAnchor = tailStart;
    tailAnchorHistory.forEach(anchor => {
      const distance = Math.max(Math.abs(anchor.x - lastAnchor.x), Math.abs(anchor.y - lastAnchor.y));
      if (distance > lengthLeft) {
        return;
      }
      // append this anchor
      tailAnchors.push(new Two.Anchor(anchor.x, anchor.y));
      lastAnchor = anchor;
      lengthLeft = lengthLeft - distance;
      historyUsed = historyUsed + 1;    
    });

    // append final anchor
    if (lengthLeft > 0) {
      switch (lastAnchor.direction) {
        case 'left': tailAnchors.push(new Two.Anchor(lastAnchor.x + lengthLeft, lastAnchor.y)); break;
        case 'right': tailAnchors.push(new Two.Anchor(lastAnchor.x - lengthLeft, lastAnchor.y)); break;
        case 'down': tailAnchors.push(new Two.Anchor(lastAnchor.x, lastAnchor.y - lengthLeft)); break;
        case 'up': default:  tailAnchors.push(new Two.Anchor(lastAnchor.x, lastAnchor.y + lengthLeft));
      }
    }
    
    // clean up anchor history
    this.tailAnchorHistory = tailAnchorHistory.slice(0, historyUsed);

    if (this.tail !== undefined) {
      this.tail.remove();
    }
    const tail = this.tail = new Two.Path(tailAnchors, false);
    tail.noFill();
    tail.linewidth = SIZE - 2;
    this.two.add(this.tail);

  }

  move(translation, distance, direction) {
    const {width, height} = this.two;
    switch (direction) {
      case 'left':
      translation.x = Math.max(HALF_SIZE, translation.x - distance);
      break;
      case 'right':
      translation.x = Math.min(width - HALF_SIZE, translation.x + distance);
      break;
      case 'down':
        translation.y = Math.min(height - HALF_SIZE, translation.y + distance);
        break;
      case 'up':
      default:
        Math.max(HALF_SIZE, translation.y = translation.y - distance);
    }
  }

  animate(frameCount, timeDelta) {
    if (timeDelta === undefined) return; // initial call
    if (this.alive === false) return;

    if (this.isCollision()) {
      this.head.fill = 'rgba(200, 50, 50, 1)';
      this.alive = false;
      return;
    }
    const distance = this.calculateDistance(timeDelta);
    const head = this.head;
    this.move(head.translation, distance, this.direction);
    this.eatApple();
    this.drawTail();

    this.head.remove();
    this.two.add(this.head);
  }

  render() {
    return (
      <div>
        <div ref='stage' style={{height: '100%'}}></div>
      </div>
    )
  }
}

export default Snake



// WEBPACK FOOTER //
// ./src/components/Snake.js